import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { AsyncThunkAction, unwrapResult } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import dayjs, { Dayjs } from 'dayjs';

import { AdminTypes } from '../../../components/Menu';
import { CustomButton, CustomSelect, CustomTable, MultipleSelect, Picker } from '../../../shared/uiComponents';
import { getCaseCoordinators } from '../../../redux/State/clientSlice/userClientSlice';
import { getSessionTypes } from '../../../redux/State/clientSlice/sessionSlice';
import { SessionTypeProperties } from '../../../redux/API/ClientAPIHelpers/calendarProperties';
import { errorNormalizer, dateNormalizer } from '../../../shared/Helpers/functions';
import { DEFAULT_TIMEZONE_ID } from '../../../shared/Helpers/timezones';
import { getBranchId } from '../../../redux/API/Main/helpers';
import { REPORT_GENERATION_MESSAGE } from '../../../shared/Helpers/constants';
import { DispatchProperties, useSelector } from '../../../redux/store';
import { GetScheduleAllotmentReportProperties, GetScheduleReportProperties, ScheduleReportStatus } from '../../../redux/API/ClientAPIHelpers/scheduleReportProperties';
import {
	downloadCancellationReportCsv,
	downloadScheduleAllotmentReportCsv,
	downloadScheduleReportCsv,
	downloadScheduleUtilizationReportCsv,
	getCancellationReportRows,
	getScheduleAllotmentReportRows,
	getScheduleReportCacheStatus,
	getScheduleReportRows,
	getScheduleUtilizationReportRows,
	refreshScheduleReportCache,
	resetScheduleReportRows,
} from '../../../redux/State/clientSlice/scheduleReportSlice';
import { ReportFilters, downloadFile } from '../helpers';
import {
	AllotmentHeaders,
	AllotmentTableBody,
	CancellationHeaders,
	CancellationStatusLabels,
	CancellationTableBody,
	OutstandingSessionsHeaders,
	OutstandingSessionsTableBody,
	ScheduleReportStatusLabels,
	UtilizationHeaders,
	UtilizationTableBody,
} from './tableInfo';

type FetchAction = AsyncThunkAction<unknown, GetScheduleReportProperties, Record<string, never>>;
type DownloadAction = AsyncThunkAction<{ file: Blob; fileName: string }, GetScheduleReportProperties, Record<string, never>>;

interface ScheduleReportBaseProperties {
	downloadAction: (data: GetScheduleReportProperties) => DownloadAction;
	fetchAction: (data: GetScheduleReportProperties) => FetchAction;
	headers: Parameters<typeof CustomTable>[0]['headers'];
	rows: Parameters<typeof CustomTable>[0]['data'];
	TableBody: FC<any>;
	usesCache?: boolean;
	showStatusFilter?: boolean;
	showCancellationStatusFilter?: boolean;
	defaultSessionType?: number;
}

const EXCLUDED_SESSION_TYPES = [8, 9];

const ScheduleReportBase = ({ downloadAction, fetchAction, headers, rows, TableBody, usesCache = false, showStatusFilter = false, showCancellationStatusFilter = false, defaultSessionType }: ScheduleReportBaseProperties) => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();
	const [caseManagerId, setCaseManagerId] = useState<string>('');
	const [sessionTypeIds, setSessionTypeIds] = useState<string[]>([]);
	const [statusIds, setStatusIds] = useState<string[]>([]);
	const [cancellationStatusId, setCancellationStatusId] = useState<string>('');
	const [sessionTypesData, setSessionTypesData] = useState<SessionTypeProperties[]>([]);

	const loading = useSelector((state) => state.scheduleReport.loading);
	const cacheStatus = useSelector((state) => state.scheduleReport.cacheStatus);
	const role = useSelector((state) => state.account.role);
	const isAdmin = role?.section.id === AdminTypes.admin;
	const caseManagers = useSelector((state) => state.userClient.caseCoordinators);
	const { endDate, startDate, clientIds, userId, timeError } = useSelector((state) => state.billing.billingFilters);

	const caseManagerOptions = useMemo(
		() => [{ id: '', name: 'All Case Managers' }, ...(caseManagers ?? []).map((x) => ({ id: x.id, name: x.fullName }))],
		[caseManagers],
	);

	const sessionTypeOptions = useMemo(
		() => sessionTypesData
			.filter((x) => x.id && !EXCLUDED_SESSION_TYPES.includes(x.type))
			.map((x) => ({ id: String(x.id), name: x.name })),
		[sessionTypesData],
	);

	const statusOptions = useMemo(
		() => Object.entries(ScheduleReportStatusLabels).map(([id, name]) => ({ id, name })),
		[],
	);

	const cancellationStatusOptions = useMemo(
		() => [{ id: '', name: 'All Statuses' }, ...Object.entries(CancellationStatusLabels).map(([id, name]) => ({ id, name }))],
		[],
	);

	const requestParams = useMemo<GetScheduleReportProperties | null>(() => {
		const page = params.get('page') || '1';
		const pageSize = params.get('pageSize') || '8';
		const direction = params.get('direction') || '';
		const orderBy = params.get('orderBy') || '';
		const searchString = params.get('searchString') || '';

		if (timeError || !startDate || !endDate) return null;

		return {
			page,
			pageSize,
			startDate,
			endDate,
			direction,
			orderBy,
			searchString,
			clientIds: clientIds.length ? clientIds : undefined,
			caseManagerId: caseManagerId || undefined,
			providerId: userId || undefined,
			sessionTypeIds: usesCache && sessionTypeIds.length ? sessionTypeIds : undefined,
			statuses: showStatusFilter && statusIds.length ? statusIds : undefined,
			cancellationStatusFilter: showCancellationStatusFilter && cancellationStatusId ? cancellationStatusId : undefined,
		};
	}, [caseManagerId, cancellationStatusId, clientIds, endDate, usesCache, showStatusFilter, showCancellationStatusFilter, params, sessionTypeIds, startDate, statusIds, timeError, userId]);

	const fetchData = useCallback(() => {
		if (!requestParams) return;
		if (usesCache) dispatch(getScheduleReportCacheStatus(requestParams)).then(unwrapResult).catch(errorNormalizer);
		dispatch(fetchAction(requestParams)).then(unwrapResult).catch(errorNormalizer);
	}, [dispatch, fetchAction, usesCache, requestParams]);

	const downloadHandler = useCallback(() => {
		if (!requestParams) return;
		toast(REPORT_GENERATION_MESSAGE);
		dispatch(downloadAction({ ...requestParams, page: '1', pageSize: `${rows.totalNumberOfItems ?? 10000}` }))
			.then(unwrapResult)
			.then(downloadFile)
			.catch(errorNormalizer);
	}, [dispatch, downloadAction, requestParams, rows.totalNumberOfItems]);

	const prevIsRunningRef = useRef<boolean>(false);

	const refreshCacheHandler = useCallback(() => {
		if (!requestParams) return;
		dispatch(refreshScheduleReportCache(requestParams)).then(unwrapResult).catch(errorNormalizer);
	}, [dispatch, requestParams]);

	useEffect(() => {
		dispatch(resetScheduleReportRows());
		dispatch(getCaseCoordinators()).then(unwrapResult).catch(errorNormalizer);
		if (usesCache) {
			dispatch(getSessionTypes({}))
				.then(unwrapResult)
				.then((response) => {
					if (!response) return;
					const sessionTypesResponse = response as SessionTypeProperties[];
					setSessionTypesData(sessionTypesResponse);

					if (defaultSessionType) {
						const match = sessionTypesResponse.find((x) => x.type === defaultSessionType);
						if (match?.id) setSessionTypeIds([String(match.id)]);
					}
				})
				.catch(errorNormalizer);
		}
	}, [dispatch, usesCache, defaultSessionType]);

	useEffect(() => {
		const timeout = setTimeout(fetchData, 1000);
		return () => clearTimeout(timeout);
	}, [fetchData]);

	useEffect(() => {
		const isRunning = cacheStatus?.isRunning ?? false;
		const wasRunning = prevIsRunningRef.current;
		prevIsRunningRef.current = isRunning;

		if (isRunning && requestParams && usesCache) {
			const id = setInterval(() => {
				dispatch(getScheduleReportCacheStatus(requestParams)).then(unwrapResult).catch(errorNormalizer);
			}, 30_000);
			return () => clearInterval(id);
		}

		if (wasRunning && !isRunning && requestParams) {
			dispatch(fetchAction(requestParams)).then(unwrapResult).catch(errorNormalizer);
		}
	}, [cacheStatus?.isRunning, dispatch, fetchAction, usesCache, requestParams]);

	const caseManagerHandler = (event: SelectChangeEvent<string>) => setCaseManagerId(event.target.value);
	const sessionTypeHandler = (event: SelectChangeEvent<string[]>) => setSessionTypeIds(event.target.value as string[]);
	const statusHandler = (event: SelectChangeEvent<string[]>) => setStatusIds(event.target.value as string[]);
	const cancellationStatusHandler = (event: SelectChangeEvent<string>) => setCancellationStatusId(event.target.value);
	const isDateOutOfRange = usesCache && !cacheStatus?.isAvailable && !cacheStatus?.isRunning && !!cacheStatus?.dateRangeStart;
	const lastRunText = cacheStatus?.completedAt
		? `Last run: ${new Date(cacheStatus.completedAt).toLocaleDateString()} ${new Date(cacheStatus.completedAt).toLocaleTimeString()}`
		: null;
	const cacheStatusText = cacheStatus?.isRunning
		? 'Report data is loading in the background — come back in a few minutes.'
		: isDateOutOfRange
			? `Showing data from ${new Date(cacheStatus!.dateRangeStart!).toLocaleDateString()} (earliest available). Select a start date within this range for full results.`
			: null;
	// Compare Eastern wall clocks, matching the server's cooldown rule.
	const cooldownMinutes = cacheStatus?.nextRefreshAvailableAt
		? Math.max(
				1,
				Math.round(
					dayjs.utc(cacheStatus.nextRefreshAvailableAt).diff(
						dayjs().tz(DEFAULT_TIMEZONE_ID).utc(true),
						'minute',
						true,
					),
				),
			)
		: null;
	const cooldownMessage = cooldownMinutes !== null
		? cooldownMinutes >= 60
			? `Next refresh available in ${Math.round(cooldownMinutes / 60)} hour${Math.round(cooldownMinutes / 60) === 1 ? '' : 's'}.`
			: `Next refresh available in ${cooldownMinutes} minute${cooldownMinutes === 1 ? '' : 's'}.`
		: null;

	return (
		<>
			<ReportFilters
				showUsers
				isMultipleSelect
				showOnlyNew={false}
				combineDateWithShortcuts
				shortcutsLabel="Date range"
				rowOneEnd={
					<div style={{ alignSelf: 'flex-end', marginLeft: 'auto' }}>
						<CustomButton onClick={downloadHandler} title="Download CSV" loading={loading} disabled={timeError} />
					</div>
				}
				afterPeriod={
					usesCache && isAdmin ? (
						<div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
							<CustomButton
								onClick={refreshCacheHandler}
								title="Refresh Cache"
								disabled={cacheStatus?.canRefresh === false || timeError}
							/>
						</div>
					) : undefined
				}
			>
				<div style={{ width: '240px' }}>
					<CustomSelect
						label="Case Manager:"
						data={caseManagerOptions}
						value={caseManagerId}
						setValue={caseManagerHandler}
					/>
				</div>
				{usesCache && (
					<div style={{ width: '240px' }}>
						<MultipleSelect
							label="Session Type:"
							data={sessionTypeOptions}
							initialValue={sessionTypeIds}
							setValue={sessionTypeHandler}
						/>
					</div>
				)}
				{showStatusFilter && (
					<div style={{ width: '240px' }}>
						<MultipleSelect
							label="Status:"
							data={statusOptions}
							initialValue={statusIds}
							setValue={statusHandler}
						/>
					</div>
				)}
				{showCancellationStatusFilter && (
					<div style={{ width: '240px' }}>
						<CustomSelect
							label="Status:"
							data={cancellationStatusOptions}
							value={cancellationStatusId}
							setValue={cancellationStatusHandler}
						/>
					</div>
				)}
			</ReportFilters>
			{usesCache && (lastRunText || cacheStatusText || cooldownMessage) && (
				<div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
					{isAdmin && lastRunText && <div>{lastRunText}</div>}
					{!isDateOutOfRange && cacheStatusText && <div>{cacheStatusText}</div>}
					{isDateOutOfRange && cacheStatusText && (
						<div style={{ color: '#b45309' }}>{cacheStatusText}</div>
					)}
					{isAdmin && cooldownMessage && (
						<div style={{ color: '#b45309' }}>{cooldownMessage}</div>
					)}
				</div>
			)}
			<CustomTable headers={headers} TableBody={TableBody} data={rows} loading={loading} hasMobile={false} />
		</>
	);
};

export const ScheduleReport = () => {
	const rows = useSelector((state) => state.scheduleReport.rows);
	return (
		<ScheduleReportBase
			downloadAction={downloadScheduleReportCsv}
			fetchAction={getScheduleReportRows}
			headers={OutstandingSessionsHeaders}
			rows={rows}
			TableBody={OutstandingSessionsTableBody}
			usesCache
			showStatusFilter
			defaultSessionType={3} // Behavior Treatment (97153)
		/>
	);
};

export const CancellationReport = () => {
	const rows = useSelector((state) => state.scheduleReport.cancellationRows);
	return (
		<ScheduleReportBase
			downloadAction={downloadCancellationReportCsv}
			fetchAction={getCancellationReportRows}
			headers={CancellationHeaders}
			rows={rows}
			TableBody={CancellationTableBody}
			showCancellationStatusFilter
		/>
	);
};

export const ScheduleUtilizationReport = () => {
	const rows = useSelector((state) => state.scheduleReport.utilizationRows);
	return (
		<ScheduleReportBase
			downloadAction={downloadScheduleUtilizationReportCsv}
			fetchAction={getScheduleUtilizationReportRows}
			headers={UtilizationHeaders}
			rows={rows}
			TableBody={UtilizationTableBody}
			usesCache
		/>
	);
};

export const ScheduleAllotmentReport = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();
	const [caseManagerId, setCaseManagerId] = useState<string>('');
	const [asOfDate, setAsOfDate] = useState<Dayjs>(dayjs());

	const rows = useSelector((state) => state.scheduleReport.allotmentRows);
	const loading = useSelector((state) => state.scheduleReport.loading);
	const caseManagers = useSelector((state) => state.userClient.caseCoordinators);
	const { clientIds } = useSelector((state) => state.billing.billingFilters);

	const caseManagerOptions = useMemo(
		() => [{ id: '', name: 'All Case Managers' }, ...(caseManagers ?? []).map((x) => ({ id: x.id, name: x.fullName }))],
		[caseManagers],
	);

	const requestParams = useMemo<GetScheduleAllotmentReportProperties>(() => {
		const page = params.get('page') || '1';
		const pageSize = params.get('pageSize') || '8';
		const direction = params.get('direction') || '';
		const orderBy = params.get('orderBy') || '';
		const searchString = params.get('searchString') || '';

		return {
			page,
			pageSize,
			direction,
			orderBy,
			searchString,
			caseManagerId: caseManagerId || undefined,
			clientIds: clientIds.length ? clientIds : undefined,
			asOfDate: asOfDate ? dateNormalizer(asOfDate) : undefined,
			branchId: getBranchId() || undefined,
		};
	}, [asOfDate, caseManagerId, clientIds, params]);

	const fetchData = useCallback(() => {
		dispatch(getScheduleAllotmentReportRows(requestParams)).then(unwrapResult).catch(errorNormalizer);
	}, [dispatch, requestParams]);

	const downloadHandler = useCallback(() => {
		toast(REPORT_GENERATION_MESSAGE);
		dispatch(downloadScheduleAllotmentReportCsv({ ...requestParams, page: '1', pageSize: `${rows.totalNumberOfItems ?? 10000}` }))
			.then(unwrapResult)
			.then(downloadFile)
			.catch(errorNormalizer);
	}, [dispatch, requestParams, rows.totalNumberOfItems]);

	useEffect(() => {
		dispatch(getCaseCoordinators()).then(unwrapResult).catch(errorNormalizer);
	}, [dispatch]);

	useEffect(() => {
		const timeout = setTimeout(fetchData, 1000);
		return () => clearTimeout(timeout);
	}, [fetchData]);

	const caseManagerHandler = (event: SelectChangeEvent<string>) => setCaseManagerId(event.target.value);
	const asOfDateHandler = (date: Dayjs | null) => {
		if (!date) return;
		setAsOfDate(date);
	};

	return (
		<>
			<ReportFilters
				showUsers={false}
				isMultipleSelect
				showOnlyNew={false}
				showDateRange={false}
				showShorcuts={false}
				inline
				afterPeriod={
					<>
						<Picker.CustomDate label="As Of Date" value={asOfDate} onChange={asOfDateHandler} />
						<div style={{ marginTop: '16px', marginLeft: 'auto' }}>
							<CustomButton onClick={downloadHandler} title="Download CSV" loading={loading} />
						</div>
					</>
				}
			>
				<div style={{ width: '240px' }}>
					<CustomSelect
						label="Case Manager:"
						data={caseManagerOptions}
						value={caseManagerId}
						setValue={caseManagerHandler}
					/>
				</div>
			</ReportFilters>
			<CustomTable headers={AllotmentHeaders} TableBody={AllotmentTableBody} data={rows} loading={loading} hasMobile={false} />
		</>
	);
};

export default ScheduleReport;
